// Framework-agnostic Pixi world renderer (distilled from the old Lobby scene).
// Owns its own full-window Pixi Application and draws ONLY the in-world canvas:
// the map tiles, Y-sorted objects + avatars, the over-player "ue" layer,
// click/keyboard movement, the camera, and the door portals. The renderer fills
// the whole viewport at native resolution (no fixed 1024×768 box / letterbox),
// bigger windows simply see more of the map. All HUD/chat is the Svelte
// overlay's job (World.svelte), this class just reports area + player count
// through `onHud`. Map warps happen in-place (no app teardown).

import { Application, Container, Graphics, Sprite, Text, Texture, Assets } from "pixi.js";
import { ChibiAvatar, chibiFrom } from "../../render/ChibiAvatar.ts";
import { NpcChibi } from "../../render/NpcChibi.ts";
import { asset, findMap, type WorldData } from "../../data.ts";
import type { WorldSocket } from "../../net/socket.ts";
import type { Facing, PlayerSnap, ServerMessage } from "@ygoevo/world-protocol";
import { portalsFor, spawnFor, titleFor, type SpawnPoint } from "./mapGraph.ts";
import { sceneMap, unpackBits, type SceneMap, type SceneObject } from "./sceneMaps.ts";
import { npcsFor, type WorldNpc } from "./npcs.ts";
import { interactablesFor, type InteractKind } from "./interactables.ts";
import { qualityMaxFps, effectiveTier } from "../stores/settings.svelte.ts";
import { worldScaleForViewport } from "./viewport.ts";

// Walk speed in world px/SECOND (frame-rate independent, the old fixed px/frame
// step ran at the monitor's refresh rate, so the avatar rocketed on 120/144 Hz
// displays). ~280 px/s is a steady, deliberate walk across the city maps.
const WALK_SPEED = 280;
// Cap the per-frame delta so a long stall (backgrounded tab) can't teleport the
// avatar across, and through, a wall in one step (collision is point-sampled).
const MAX_FRAME_MS = 32;
// Camera chase rate (per second). The camera eases toward the player instead of
// snapping 1:1, which reads smoother. Higher = tighter/snappier follow.
// ponytail: framerate-independent (exp smoothing), so 60Hz and 144Hz match, tune this one knob.
const CAM_LERP = 9;
const AVATAR_DISPLAY_H = 150;
const WORLD_UI_MAX_WIDTH = 2560;
// Collision grid resolution: 1 cell per N world px. The atari hitmaps are 256px
// tiles, so 4px cells are plenty fine (smaller than one walk STEP) while keeping
// the whole-map grid tiny (~290KB for the biggest 2560×1792 map).
const COLLISION_SCALE = 4;

// Door triggers are read straight from the atari hitmap. In the YO2 hitmaps the
// map exits are painted as solid COLOURED regions (blue/cyan/purple variants),
// distinct from black (=wall), red (=a second walkable surface), and transparent
// (=open ground). Each coloured region is one doorway, drawn by Konami exactly
// over the road/door the art shows, so we trigger warps off those rectangles
// instead of hand-placed circles. A pixel is a warp cell when it's strongly blue
// (high B, low-ish R) and not white. See mapGraph.ts for the destination wiring.
const isWarpPixel = (r: number, g: number, b: number): boolean =>
  b >= 150 && r <= 170 && !(r > 200 && g > 200);
// Smallest connected warp region (in collision cells) we treat as a real door,
// filters stray antialiased pixels along coloured edges.
const MIN_ZONE_CELLS = 24;
// How far (world px) a hand-authored door may sit from a real atari zone and
// still snap onto it. Generous enough to rescue doors placed at a map's centre
// when the real exit is off in a corner (the classic "walk through the door, no
// warp" bug, the worst real case is ~760px), tight enough not to grab an
// unrelated exit on multi-door maps (the nearest non-door pair sits ~975px out).
const ZONE_BIND_MAX = 820;
// Inflate a zone's trigger rect by a few px so stepping onto the doorway edge
// fires the warp (matches the feel of the old radius trigger).
const PORTAL_PAD = 8;

/** A door/warp region read from a map's atari hitmap (one coloured blob). All
 *  fields are world px. */
interface WarpZone {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  cx: number;
  cy: number;
  cells: number;
}

/** A live door trigger: the authored destination (from mapGraph) paired with a
 *  trigger that is EITHER a real atari warp rectangle (preferred) or the legacy
 *  hand-placed circle (fallback when no zone is near). */
interface ActivePortal {
  to: string;
  label: string;
  /** Trigger centre, zone centroid, or the nudged hand coord when rect is null. */
  cx: number;
  cy: number;
  rect: { x0: number; y0: number; x1: number; y1: number } | null;
  /** Used only when rect is null. */
  radius: number;
}

type LoginOk = Extract<ServerMessage, { t: "LoginOk" }>;

export interface WorldHud {
  area: string;
  players: number;
}

export interface WorldRendererOpts {
  socket: WorldSocket;
  data: WorldData;
  loginOk: LoginOk;
  onHud: (h: WorldHud) => void;
  /** Player clicked an overworld NPC, open its conversation dialog. */
  onNpcClick?: (npc: WorldNpc) => void;
  /** Player clicked another player's chibi, open their player card. */
  onPlayerClick?: (userId: string) => void;
  /** Player clicked an interactive fixture (e.g. the card shop). */
  onInteract?: (kind: InteractKind, id: string) => void;
  /** Load progress (0..1 fraction + coarse phase label). Drives the boot loader on
   *  mount AND the map-switch loader on every later warp/door transition. */
  onProgress?: (p: { value: number; phase: string }) => void;
  /** A map switch (door or fast-travel) begins/ends, drives the inter-map loading
   *  screen. `active:true` at the start (with the destination area name), `false`
   *  once the new map is painted and the player placed. Never fires on first mount. */
  onTransition?: (s: { active: boolean; area: string }) => void;
}

interface RemotePlayer {
  avatar: ChibiAvatar;
  container: Container;
  targetX: number;
  targetY: number;
  name: string;
  /** "DUELING" badge over the head, shown while this player is in a duel. */
  duelBadge: Container;
}

interface SpeechBubble {
  view: Container;
  expiresAt: number;
  fadeAt: number;
}

export class WorldRenderer {
  private app = new Application();
  private camera = new Container();
  private worldScale = 1;
  // Float camera position the chase lerps; `camera.x/y` is this floored, so tiles
  // stay pixel-crisp while the follow stays sub-pixel smooth.
  private camX = 0;
  private camY = 0;
  private base = new Container();
  private midground = new Container();
  private overlay = new Container();
  private ue = new Container();
  // Themed fill behind the map: maps are ~2048px wide and render 1:1, so any viewport wider than
  // the map (ultrawide, even plain 1440p) used to show the raw clear colour — "the black bevel".
  // A static cyber grid on the stage (doesn't scroll) + a thin frame around the map bounds (in the
  // camera) make the overflow read as a designed holo-table instead of dead space.
  private backdrop = new Graphics();
  private mapFrame = new Graphics();

  private opts: WorldRendererOpts;
  private mapId = "map_r01";
  private mapWidth = 0;
  private mapHeight = 0;
  /** Walkability grid for the current map (1 = blocked) at COLLISION_SCALE px
   *  per cell, baked from the map's atari hitmap tiles. Null until built. */
  private collision: Uint8Array | null = null;
  private collisionW = 0;
  private collisionH = 0;
  /** Door/warp rectangles for the current map, read from its atari hitmap. */
  private warpZones: WarpZone[] = [];
  /** Pre-baked collision grids + warp zones keyed by map id, so re-entering (or
   *  arriving at a preloaded neighbour) skips the atari fetch + canvas bake. */
  private collisionCache = new Map<
    string,
    { grid: Uint8Array; w: number; h: number; zones: WarpZone[] }
  >();
  /** Map ids whose assets we've already kicked off a background preload for. */
  private preloaded = new Set<string>();

  private localAvatar: ChibiAvatar | null = null;
  private localContainer = new Container();
  private localX = 0;
  private localY = 0;
  private localFacing: Facing = "s";

  private players = new Map<string, RemotePlayer>();
  /** Active speech bubbles, keyed by speaker display name. One per speaker, a new
   *  line from the same speaker replaces the previous bubble. Pinned to the speaker's
   *  player container so it scrolls/walks with them for free. */
  private bubbles = new Map<string, SpeechBubble>();
  /** Static overworld NPCs (the GX/DM cast) spawned for the current map. */
  private npcs: { container: Container; chibi: NpcChibi }[] = [];
  /** Clickable map fixtures (e.g. the card shop) for the current map. */
  private interactables: Container[] = [];
  /** Invisible door triggers for the current map (atari zones + circle fallback). */
  private portals: ActivePortal[] = [];
  private portalArmed = false;
  private transitioning = false;
  /** Freezes local movement (used while a blocking modal, e.g. the first-join
   *  structure-deck pick, is up, so the player can't walk the world behind it). */
  private paused = false;

  private keys = new Set<string>();
  /** Click-to-walk route: world-px waypoints to walk in order (A* over the
   *  collision grid, string-pulled to long straight runs). Empty = not walking. */
  private walkPath: { x: number; y: number }[] = [];
  private lastSentMove = 0;

  private unbinds: Array<() => void> = [];
  private destroyed = false;
  /** True only during the very first map load (mount). Gates the per-tile boot
   *  progress so in-place warps stay silent. */
  private booting = false;

  constructor(opts: WorldRendererOpts) {
    this.opts = opts;
  }

  /** Push a boot-progress sample to the loader (initial mount only). */
  private boot(value: number, phase: string): void {
    this.opts.onProgress?.({ value: Math.min(1, value), phase });
  }

  /** Initialise the Pixi app, build the local avatar, load the first map. */
  async mount(parent: HTMLElement, mapId: string, spawn: SpawnPoint): Promise<void> {
    this.boot(0.08, "Initializing renderer");
    await this.app.init({
      width: window.innerWidth,
      height: window.innerHeight,
      backgroundColor: 0x05070f,
      antialias: false,
      autoDensity: false,
      resolution: 1,
      preference: "webgpu", // Pixi picks its WebGPU renderer (matches the three scenes; no WebGL)
    });
    if (this.destroyed) {
      this.app.destroy({ removeView: true });
      return;
    }
    const c = this.app.canvas;
    c.style.position = "absolute";
    c.style.inset = "0";
    c.style.width = "100%";
    c.style.height = "100%";
    c.style.imageRendering = "pixelated";
    parent.appendChild(c);
    this.resize();
    window.addEventListener("resize", this.resize);
    this.unbinds.push(() => window.removeEventListener("resize", this.resize));

    this.app.stage.addChild(this.backdrop, this.camera);
    this.camera.addChild(this.base, this.midground, this.overlay, this.ue, this.mapFrame);
    this.drawBackdrop();
    this.midground.sortableChildren = true;

    // Local chibi avatar (persists across map warps), rendered from the player's
    // saved look.
    const look = chibiFrom(this.opts.loginOk.avatar);
    this.localAvatar = new ChibiAvatar({
      manifest: this.opts.data.chibi,
      gender: look.gender,
      selection: look.selection,
      displayHeight: AVATAR_DISPLAY_H,
    });
    await this.localAvatar.build();
    void this.localAvatar.prewarm(); // cache walk frames now so the first step doesn't glide in the idle pose
    this.localContainer.addChild(this.localAvatar.root);
    this.boot(0.14, "Compiling avatar");

    this.installInput();
    this.bindNetwork();
    this.app.ticker.add((t) => this.update(t.deltaMS));
    // Cap the (otherwise uncapped, refresh-rate) render loop: Low → 30fps (a top-down walker reads
    // fine at 30, and rendering the whole scene 60–120×/s while the player stands around is what pegs
    // a weak iGPU's fans), Medium/High → 60. Movement is deltaMS-based, so the cap can't affect speed.
    this.applyQuality();
    this.applyRendering(); // honour a render-pause requested before the ticker existed (e.g. customizer open on mount)

    // Dev handle: lets headless/throttled preview pump the ticker for screenshots.
    const g = globalThis as Record<string, unknown>;
    g.__df = { ...((g.__df as Record<string, unknown>) ?? {}), worldApp: this.app };

    // Drive the boot loader off this first map's real asset loads, then settle.
    this.booting = true;
    await this.enterMap(mapId, spawn);
    this.booting = false;
    this.boot(1, "Materializing");
  }

  /** Where the player is standing right now, stashed by the World screen on unmount so a later
   *  remount (returning from a duel / deck / leaderboard) restores it instead of the map's spawn. */
  currentSpawn(): SpawnPoint {
    return { x: this.localX, y: this.localY, facing: this.localFacing };
  }

  /** Match the renderer to the full window. Conventional viewports stay at 1:1;
   *  desktop ultrawide viewports switch to the full 2× presentation. */
  private resize = (): void => {
    this.app.renderer.resize(window.innerWidth, window.innerHeight);
    this.applyViewportScale();
    this.drawBackdrop();
  };

  private applyViewportScale(): void {
    this.worldScale = worldScaleForViewport(this.app.screen.width, this.app.screen.height);
    this.camera.scale.set(this.worldScale);
  }

  /** Static viewport-sized cyber grid behind the camera — what shows where the map doesn't reach. */
  private drawBackdrop(): void {
    const w = window.innerWidth, h = window.innerHeight;
    const g = this.backdrop;
    g.clear();
    g.rect(0, 0, w, h).fill(0x060411);

    // A centred command field gives very wide monitors a deliberate visual
    // boundary and matches the HUD's safe frame. It is mostly hidden by large
    // maps, but gives compact rooms a composed holo-table instead of raw void.
    const fieldW = Math.min(w, WORLD_UI_MAX_WIDTH);
    const fieldX = (w - fieldW) / 2;
    g.rect(fieldX, 0, fieldW, h).fill({ color: 0x0b1830, alpha: 0.34 });

    const STEP = 64;
    for (let x = 0.5; x <= w; x += STEP) g.moveTo(x, 0).lineTo(x, h);
    for (let y = 0.5; y <= h; y += STEP) g.moveTo(0, y).lineTo(w, y);
    g.stroke({ width: 1, color: 0x1b3a52, alpha: 0.3 });

    if (fieldX > 0) {
      g.moveTo(fieldX + 0.5, 0).lineTo(fieldX + 0.5, h);
      g.moveTo(fieldX + fieldW - 0.5, 0).lineTo(fieldX + fieldW - 0.5, h);
      g.stroke({ width: 1, color: 0x2ca0ff, alpha: 0.2 });
    }
  }

  /** Thin frame at the current map's bounds so its edge reads as a panel border, not a crop. */
  private drawMapFrame(): void {
    const g = this.mapFrame;
    g.clear();
    g.rect(-1.5, -1.5, this.mapWidth + 3, this.mapHeight + 3).stroke({ width: 2, color: 0x36d6ff, alpha: 0.28 });
    g.rect(-6, -6, this.mapWidth + 12, this.mapHeight + 12).stroke({ width: 1, color: 0x36d6ff, alpha: 0.1 });
  }

  // ── map switching (in-place warp) ───────────────────────────────────────────
  private async enterMap(mapId: string, spawn: SpawnPoint): Promise<void> {
    this.mapId = mapId;
    // Dispatch on format: a scene map (single AI-authored PNG + painted grid) or
    // a legacy tile map (256px grid + atari hitmap). Both end up with the same
    // collision grid + portal triggers, so everything downstream is shared.
    const scene = sceneMap(mapId);
    const map = scene ? null : findMap(this.opts.data, mapId);
    if (!scene && !map) throw new Error(`unknown map: ${mapId}`);
    this.mapWidth = scene ? scene.width : map!.cols * map!.partsW;
    this.mapHeight = scene ? scene.height : map!.rows * map!.partsH;
    this.applyViewportScale();
    this.drawMapFrame();

    // Inter-map loading screen: announce the switch (the boot loader owns the very
    // first mount, so stay silent while booting). Cache hits finish before the
    // overlay's anti-flash delay elapses, so a warm warp shows nothing.
    const area = titleFor(mapId);
    if (!this.booting) this.opts.onTransition?.({ active: true, area });

    // Clear previous map content + remotes + portals.
    this.base.removeChildren();
    this.midground.removeChildren();
    this.overlay.removeChildren();
    this.ue.removeChildren();
    for (const r of this.players.values()) r.container.destroy({ children: true });
    this.players.clear();
    for (const n of this.npcs) n.chibi.destroy();
    this.npcs = [];
    this.interactables = [];
    this.portals = [];
    // Drop any in-flight bubbles, their parent containers are about to be destroyed
    // and the local one would dangle on a new map.
    for (const b of this.bubbles.values()) b.view.destroy({ children: true });
    this.bubbles.clear();

    // Re-attach the click hit-area + local avatar to the fresh midground.
    this.installClickToWalk();
    this.midground.addChild(this.localContainer);

    if (scene) await Promise.all([this.loadSceneMap(scene), this.buildSceneCollision(scene)]);
    else await Promise.all([this.loadMap(map!), this.buildCollision(map!)]);
    this.buildPortals();
    void this.spawnNpcs();
    this.spawnInteractables();

    // Place the player at the spawn (and drop any click-walk target from the
    // previous map so we don't auto-walk on arrival). Spawn points were authored
    // by eye before collision existed, so some land inside an obstacle, nudge to
    // the nearest walkable cell so the player never arrives wedged in a wall.
    const safe = this.clearArrivalZone(this.nudgeToWalkable(spawn.x, spawn.y));
    this.walkPath = [];
    this.keys.clear();
    this.localX = safe.x;
    this.localY = safe.y;
    this.localFacing = spawn.facing;
    this.localAvatar?.setFacing(spawn.facing);
    this.localContainer.x = safe.x;
    this.localContainer.y = safe.y;
    this.localContainer.zIndex = safe.y;
    this.portalArmed = false;
    this.transitioning = false;

    // Snap the camera to the new spawn so it doesn't ease across the whole map on
    // every warp, the chase lerp only kicks in for in-map movement.
    const cam = this.cameraTarget();
    this.camX = cam.x;
    this.camY = cam.y;
    this.camera.x = this.camX | 0;
    this.camera.y = this.camY | 0;

    this.emitHud();
    // Map is painted and the player is placed, drop the inter-map loading screen.
    if (!this.booting) this.opts.onTransition?.({ active: false, area });
    // Send the resolved (walkable) coords, the server echoes them back via
    // EnterMapOk and would otherwise snap us back onto the original blocked spot.
    this.opts.socket.send({ t: "EnterMap", mapId, x: safe.x, y: safe.y, facing: spawn.facing });

    // Now that this map is up, warm every map one door away in the background so
    // the next transition is a cache hit (textures + collision) instead of a
    // fresh round of fetches. Fire-and-forget; never blocks the current map.
    void this.preloadNeighbors(mapId);
  }

  /** Freeze/unfreeze local movement for a blocking modal. Clears any in-flight
   *  walk so the avatar settles to idle instead of moon-walking behind the overlay. */
  setPaused(paused: boolean): void {
    this.paused = paused;
    if (paused) {
      this.keys.clear();
      this.walkPath = [];
      if (this.localAvatar) this.localAvatar.walking = false;
    }
  }

  /** Stop/resume the render loop for a fullscreen overlay that fully covers the world (the avatar
   *  customizer): drawing the overworld at 60fps behind an opaque panel is pure wasted GPU (and fan
   *  noise). Halting the ticker freezes on the last frame; resuming picks straight back up. Not
   *  teardown — just a paused loop — so it's safe to toggle repeatedly. Stored + re-applied in mount()
   *  so a request that lands before init() (ticker not built yet) still takes effect. */
  setRendering(active: boolean): void {
    this.renderActive = active;
    this.applyRendering();
  }
  private renderActive = true;
  private applyRendering(): void {
    if (this.destroyed || !this.app?.ticker) return;
    if (this.renderActive) this.app.ticker.start();
    else this.app.ticker.stop();
  }

  /** Apply the current render-quality tier's overworld frame cap. Called on mount and whenever the
   *  tier changes (manual pick or the auto perf monitor). */
  applyQuality(): void {
    // `app.ticker` only exists after the async `app.init()` — the reactive quality $effect (and the
    // auto perf monitor bumping the tier) can fire before init finishes or after teardown, so guard it.
    // mount() calls this again right after init(), so a skipped early call still gets applied.
    if (!this.destroyed && this.app.ticker) this.app.ticker.maxFPS = qualityMaxFps(effectiveTier());
  }

  /** Fast-travel (map disk). Safe to call mid-walk; ignored while transitioning.
   *  `force` re-enters even the current map (Go-to a player standing beside you). */
  async warpTo(mapId: string, spawn: SpawnPoint, force = false): Promise<void> {
    if (this.transitioning || (!force && mapId === this.mapId)) return;
    this.transitioning = true;
    await this.enterMap(mapId, spawn);
  }

  private emitHud(): void {
    this.opts.onHud({
      area: titleFor(this.mapId),
      players: this.players.size + 1,
    });
  }

  private async loadMap(map: NonNullable<ReturnType<typeof findMap>>): Promise<void> {
    // During the initial boot, tick the loader forward as each texture settles so
    // the progress ring tracks real terrain decode (mapped into the 0.16–0.96 band
    // the mount() phases reserve for "Decrypting terrain").
    const ue = map.ue ?? [];
    const total = Math.max(
      1,
      map.cols * map.rows + map.objects.length + map.overlay.length + ue.length,
    );
    let done = 0;
    const tick = (): void => {
      done++;
      const frac = done / total;
      // Boot loader gets the reserved 0.16–0.96 band; a later warp drives its own
      // map-switch loader straight off the decode fraction.
      if (this.booting) this.boot(0.16 + frac * 0.8, "Decrypting terrain");
      else this.opts.onProgress?.({ value: Math.min(0.97, frac), phase: "Loading area" });
    };

    const tileJobs: Promise<void>[] = [];
    for (let col = 1; col <= map.cols; col++) {
      for (let row = 1; row <= map.rows; row++) {
        const path = `${map.basePath}${map.baseTilePrefix}_${col}_${row}.png`;
        tileJobs.push(
          Assets.load(asset(path))
            .then((tex: Texture) => {
              const s = new Sprite(tex);
              s.x = (col - 1) * map.partsW;
              s.y = (row - 1) * map.partsH;
              this.base.addChild(s);
            })
            .catch(() => {})
            .finally(tick),
        );
      }
    }
    await Promise.all(tileJobs);

    for (const obj of map.objects) {
      try {
        const tex = (await Assets.load(asset(obj.src))) as Texture;
        const s = new Sprite(tex);
        s.x = obj.x;
        s.y = obj.y;
        s.zIndex = obj.y + tex.height;
        this.midground.addChild(s);
      } catch {
        /* missing obj sprite, skip */
      }
      tick();
    }
    for (const obj of map.overlay) {
      try {
        const tex = (await Assets.load(asset(obj.src))) as Texture;
        const s = new Sprite(tex);
        s.x = obj.x;
        s.y = obj.y;
        this.overlay.addChild(s);
      } catch {
        /* skip */
      }
      tick();
    }
    for (const t of ue) {
      try {
        const tex = (await Assets.load(asset(t.src))) as Texture;
        const s = new Sprite(tex);
        s.x = t.x;
        s.y = t.y;
        this.ue.addChild(s);
      } catch {
        /* skip */
      }
      tick();
    }
  }

  /** Draw a scene map: one flat scene PNG as the base, plus optional foreground
   *  props. Props on the `midground` are Y-sorted by their bottom edge so the
   *  player walks behind them correctly (identical to legacy objects); `overlay`
   *  props are always-on-top (walk-under tops). Missing art fails silently. */
  private async loadSceneMap(map: SceneMap): Promise<void> {
    // Same retry as the pieces below: the scene is content-hashed, so a URL that failed
    // once (deploy race, or the browser's failed-image cache) is retried with a query.
    const loadScene = async (attempt = 0): Promise<void> => {
      try {
        const tex = (await Assets.load(asset(map.scene) + (attempt ? `?r=${attempt}` : ""))) as Texture;
        if (this.mapId !== map.id) return;
        this.base.addChildAt(new Sprite(tex), 0); // scene origin is (0,0)
      } catch {
        const delay = [2000, 8000, 30000][attempt];
        if (delay !== undefined) setTimeout(() => void loadScene(attempt + 1), delay);
      }
    };
    await loadScene();
    if (!this.booting) this.opts.onProgress?.({ value: 0.5, phase: "Loading area" });
    // A piece that fails to load is retried with a cache-busting query: right after a
    // deploy the client can go live seconds before the asset sync lands, and the browser
    // remembers a failed image URL for the rest of the session — without this the
    // avatar is drawn on top of the boulder it should be walking behind.
    const place = async (obj: SceneObject, attempt = 0): Promise<void> => {
      const url = asset(obj.src) + (attempt ? `?r=${attempt}` : "");
      try {
        const tex = (await Assets.load(url)) as Texture;
        if (this.mapId !== map.id) return; // warped away while retrying
        const s = new Sprite(tex);
        s.x = obj.x;
        s.y = obj.y;
        if (obj.layer === "overlay") {
          this.overlay.addChild(s);
        } else {
          s.zIndex = obj.y + tex.height; // bottom edge → Y-sort against the avatar
          this.midground.addChild(s);
        }
      } catch {
        const delay = [2000, 8000, 30000][attempt];
        if (delay !== undefined) setTimeout(() => void place(obj, attempt + 1), delay);
      }
    };
    await Promise.all((map.objects ?? []).map((obj) => place(obj)));
    if (!this.booting) this.opts.onProgress?.({ value: 0.97, phase: "Loading area" });
  }

  /** Install a scene map's collision, cache-first like buildCollision. Scene maps
   *  carry their door triggers on the portals themselves, so there are no warp
   *  zones to extract here (zones: []). */
  private async buildSceneCollision(map: SceneMap): Promise<void> {
    this.collision = null;
    this.warpZones = [];
    let entry = this.collisionCache.get(map.id);
    if (!entry) {
      entry = this.expandSceneCollision(map);
      this.collisionCache.set(map.id, entry);
    }
    if (this.mapId !== map.id) return; // warped away mid-build.
    this.collision = entry.grid;
    this.collisionW = entry.w;
    this.collisionH = entry.h;
    this.warpZones = entry.zones;
  }

  /** Expand a scene map's painted author grid (e.g. 16px cells) into the engine's
   *  native COLLISION_SCALE (4px) grid, so blocked(), pathfinding, and click-to-walk
   *  are byte-for-byte the same code path as legacy maps — no per-map collision scale
   *  to thread through movement. Pure w.r.t. the map (serves active + future preload). */
  private expandSceneCollision(
    map: SceneMap,
  ): { grid: Uint8Array; w: number; h: number; zones: WarpZone[] } {
    const { cell, cols, rows, bits } = map.collision;
    const author = unpackBits(bits, cols * rows);
    const S = COLLISION_SCALE;
    const gw = Math.max(1, Math.round(map.width / S));
    const gh = Math.max(1, Math.round(map.height / S));
    const grid = new Uint8Array(gw * gh);
    for (let gy = 0; gy < gh; gy++) {
      const ar = Math.min(rows - 1, ((gy * S) / cell) | 0);
      for (let gx = 0; gx < gw; gx++) {
        const ac = Math.min(cols - 1, ((gx * S) / cell) | 0);
        grid[gy * gw + gx] = author[ar * cols + ac]!;
      }
    }
    return { grid, w: gw, h: gh, zones: [] };
  }

  /** Install the current map's collision grid, preferring the pre-baked cache
   *  (set on first visit or by neighbour preloading) and only baking on a miss.
   *  Failures leave the grid null → no collision (better to let the player move
   *  than to trap them on a load error). */
  private async buildCollision(
    map: NonNullable<ReturnType<typeof findMap>>,
  ): Promise<void> {
    this.collision = null;
    this.warpZones = [];
    let entry = this.collisionCache.get(map.id);
    if (!entry) {
      const baked = await this.computeCollision(map);
      if (!baked) return; // environment failure, skip collision rather than crash.
      this.collisionCache.set(map.id, baked);
      entry = baked;
    }
    if (this.mapId !== map.id) return; // warped away while baking, keep it cached, don't apply.
    this.collision = entry.grid;
    this.collisionW = entry.w;
    this.collisionH = entry.h;
    this.warpZones = entry.zones;
  }

  /** Bake a map's atari hitmap tiles into a downsampled walkability grid
   *  (1 = solid) AND extract its door/warp zones, both from one canvas read.
   *
   *  The YO2 hitmap is a colour key, not a 1-bit mask: BLACK = wall, transparent
   *  = open ground, RED = a second walkable surface (paths/plazas, the player
   *  spawns on it), and the BLUE/CYAN/PURPLE family = map exits, each blob drawn
   *  over the doorway/road the art shows. So a cell is solid only when it is dark
   *  in ALL channels (true black), the old red-channel-only test wrongly baked
   *  every blue-ish exit as a wall, so you couldn't stand on a door. Smoothing is
   *  off so the downsample stays nearest-neighbour and keeps the colours pure.
   *
   *  Pure w.r.t. the map argument (no current-map state) so it serves both the
   *  active map and neighbour preloads. Returns null when OffscreenCanvas / 2d
   *  ctx is unavailable. */
  private async computeCollision(
    map: NonNullable<ReturnType<typeof findMap>>,
  ): Promise<{ grid: Uint8Array; w: number; h: number; zones: WarpZone[] } | null> {
    const mapW = map.cols * map.partsW;
    const mapH = map.rows * map.partsH;
    const gw = Math.max(1, Math.round(mapW / COLLISION_SCALE));
    const gh = Math.max(1, Math.round(mapH / COLLISION_SCALE));
    let canvas: OffscreenCanvas;
    try {
      canvas = new OffscreenCanvas(gw, gh);
    } catch {
      return null; // OffscreenCanvas unavailable.
    }
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = false; // keep the colour key crisp when downsampling
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, gw, gh);
    const tw = map.partsW / COLLISION_SCALE;
    const th = map.partsH / COLLISION_SCALE;
    const jobs: Promise<void>[] = [];
    for (let col = 1; col <= map.cols; col++) {
      for (let row = 1; row <= map.rows; row++) {
        const url = asset(`${map.basePath}${map.atariTilePrefix}_${col}_${row}.png`);
        jobs.push(
          fetch(url)
            .then((r) => (r.ok ? r.blob() : Promise.reject(new Error("404"))))
            .then((b) => createImageBitmap(b))
            .then((bmp) => {
              // Disjoint regions, so drawing in any order is safe.
              ctx.drawImage(bmp, (col - 1) * tw, (row - 1) * th, tw, th);
              bmp.close();
            })
            .catch(() => {}),
        );
      }
    }
    await Promise.all(jobs);
    const px = ctx.getImageData(0, 0, gw, gh).data;
    const grid = new Uint8Array(gw * gh);
    const warp = new Uint8Array(gw * gh);
    for (let i = 0, p = 0; i < grid.length; i++, p += 4) {
      const r = px[p]!;
      const g = px[p + 1]!;
      const b = px[p + 2]!;
      grid[i] = r < 128 && g < 128 && b < 128 ? 1 : 0; // true black = solid
      warp[i] = isWarpPixel(r, g, b) ? 1 : 0;
    }
    return { grid, w: gw, h: gh, zones: this.extractWarpZones(warp, gw, gh) };
  }

  /** Flood-fill the warp mask into connected door regions, returning each as a
   *  world-px rectangle + centroid. Consumes `warp` (cells are cleared as they're
   *  visited). Components smaller than MIN_ZONE_CELLS are dropped as edge noise. */
  private extractWarpZones(warp: Uint8Array, gw: number, gh: number): WarpZone[] {
    const zones: WarpZone[] = [];
    const stack: number[] = [];
    const S = COLLISION_SCALE;
    for (let start = 0; start < warp.length; start++) {
      if (warp[start] !== 1) continue;
      let minX = gw;
      let minY = gh;
      let maxX = 0;
      let maxY = 0;
      let sumX = 0;
      let sumY = 0;
      let cells = 0;
      warp[start] = 0;
      stack.push(start);
      while (stack.length) {
        const idx = stack.pop()!;
        const x = idx % gw;
        const y = (idx / gw) | 0;
        cells++;
        sumX += x;
        sumY += y;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
        if (x > 0 && warp[idx - 1] === 1) (warp[idx - 1] = 0), stack.push(idx - 1);
        if (x < gw - 1 && warp[idx + 1] === 1) (warp[idx + 1] = 0), stack.push(idx + 1);
        if (y > 0 && warp[idx - gw] === 1) (warp[idx - gw] = 0), stack.push(idx - gw);
        if (y < gh - 1 && warp[idx + gw] === 1) (warp[idx + gw] = 0), stack.push(idx + gw);
      }
      if (cells < MIN_ZONE_CELLS) continue;
      zones.push({
        x0: minX * S,
        y0: minY * S,
        x1: (maxX + 1) * S,
        y1: (maxY + 1) * S,
        cx: Math.round((sumX / cells) * S),
        cy: Math.round((sumY / cells) * S),
        cells,
      });
    }
    return zones;
  }

  /** Every texture URL a map draws: base tiles + objects + overlay + over-player
   *  layer. Used both to render the active map and to warm neighbours. */
  private mapAssetUrls(map: NonNullable<ReturnType<typeof findMap>>): string[] {
    const urls: string[] = [];
    for (let col = 1; col <= map.cols; col++) {
      for (let row = 1; row <= map.rows; row++) {
        urls.push(asset(`${map.basePath}${map.baseTilePrefix}_${col}_${row}.png`));
      }
    }
    for (const o of map.objects) urls.push(asset(o.src));
    for (const o of map.overlay) urls.push(asset(o.src));
    for (const t of map.ue ?? []) urls.push(asset(t.src));
    return urls;
  }

  /** Warm every map one door away from `fromMap` so the next transition is a
   *  cache hit: textures land in Pixi's Assets cache (where loadMap finds them
   *  instantly) and the collision grid is pre-baked into collisionCache. Runs in
   *  the background, never awaited, tolerant of missing tiles, and harmless if
   *  the player warps away mid-preload (the work just stays cached). Each map is
   *  warmed at most once for the renderer's lifetime. */
  private async preloadNeighbors(fromMap: string): Promise<void> {
    const neighbors = [...new Set(portalsFor(fromMap).map((p) => p.to))];
    for (const id of neighbors) {
      if (this.destroyed || this.preloaded.has(id)) continue;
      this.preloaded.add(id);
      const map = findMap(this.opts.data, id);
      if (!map) continue;
      // Textures: Assets.load is idempotent and de-dupes in-flight loads, so a
      // later real load (or an overlapping preload) reuses the same fetch. Load
      // each url on its own so a single missing tile can't reject the batch.
      for (const url of this.mapAssetUrls(map)) {
        void Assets.load(url).catch(() => {});
      }
      // Collision: bake once and stash so buildCollision becomes a cache hit.
      if (!this.collisionCache.has(id)) {
        void this.computeCollision(map).then((r) => {
          if (r && !this.collisionCache.has(id)) this.collisionCache.set(id, r);
        });
      }
    }
  }

  /** Is the given world point inside an obstacle (or off-map)? */
  private blocked(x: number, y: number): boolean {
    const g = this.collision;
    if (!g) return false;
    if (x < 0 || y < 0 || x >= this.mapWidth || y >= this.mapHeight) return true;
    const gx = Math.min(this.collisionW - 1, (x / COLLISION_SCALE) | 0);
    const gy = Math.min(this.collisionH - 1, (y / COLLISION_SCALE) | 0);
    return g[gy * this.collisionW + gx] === 1;
  }

  /** Nearest walkable point to (x,y), spiralling out a ring at a time. Keeps the
   *  hand-authored spawn/portal coords (placed before collision existed) usable:
   *  any that landed in an obstacle snap just inside the nearest walkable space. */
  private nudgeToWalkable(x: number, y: number): { x: number; y: number } {
    if (!this.blocked(x, y)) return { x, y };
    const max = Math.max(this.mapWidth, this.mapHeight);
    for (let r = COLLISION_SCALE; r <= max; r += COLLISION_SCALE) {
      for (let a = 0; a < 360; a += 15) {
        const rad = (a * Math.PI) / 180;
        const nx = x + Math.cos(rad) * r;
        const ny = y + Math.sin(rad) * r;
        if (!this.blocked(nx, ny)) return { x: Math.round(nx), y: Math.round(ny) };
      }
    }
    return { x, y };
  }

  /** A* over the collision grid from one world point to another. Returns the
   *  list of world-px waypoints to walk in order (string-pulled into long
   *  straight runs), or null if the target is unreachable. The grid is point-
   *  sampled at the avatar's feet exactly like blocked(), so a clear route here
   *  is a clear route to walk. Cheap enough to run once per click. */
  private computePath(
    sx: number,
    sy: number,
    tx: number,
    ty: number,
  ): { x: number; y: number }[] | null {
    const g = this.collision;
    const W = this.collisionW;
    const H = this.collisionH;
    if (!g || W === 0 || H === 0) return null;

    const cellOf = (x: number, y: number): number => {
      const gx = Math.min(W - 1, Math.max(0, (x / COLLISION_SCALE) | 0));
      const gy = Math.min(H - 1, Math.max(0, (y / COLLISION_SCALE) | 0));
      return gy * W + gx;
    };
    const start = cellOf(sx, sy);
    const goal = cellOf(tx, ty);
    // Start/goal must be walkable, callers nudge the target onto open ground
    // first, and the player can't stand in a wall.
    if (g[start] === 1 || g[goal] === 1) return null;
    if (start === goal) return [{ x: tx, y: ty }];

    const N = W * H;
    const gScore = new Float64Array(N).fill(Infinity);
    const came = new Int32Array(N).fill(-1);
    const closed = new Uint8Array(N);
    const gx0 = goal % W;
    const gy0 = (goal / W) | 0;
    // Octile heuristic (8-directional, diagonals cost √2): admissible, so A*
    // returns a shortest route.
    const heur = (i: number): number => {
      const adx = Math.abs((i % W) - gx0);
      const ady = Math.abs(((i / W) | 0) - gy0);
      return adx + ady + (Math.SQRT2 - 2) * Math.min(adx, ady);
    };

    const open = new MinHeap();
    gScore[start] = 0;
    open.push(start, heur(start));
    // With the closed set the loop pops each cell at most once, so N is a hard
    // ceiling; the cap is just a backstop against a malformed grid.
    let budget = N + 1;
    let reached = false;
    while (open.size() > 0 && budget-- > 0) {
      const cur = open.pop();
      if (closed[cur] === 1) continue;
      closed[cur] = 1;
      if (cur === goal) {
        reached = true;
        break;
      }
      const cx = cur % W;
      const cy = (cur / W) | 0;
      for (let dy = -1; dy <= 1; dy++) {
        const ny = cy + dy;
        if (ny < 0 || ny >= H) continue;
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const nx = cx + dx;
          if (nx < 0 || nx >= W) continue;
          const ni = ny * W + nx;
          if (g[ni] === 1 || closed[ni] === 1) continue;
          const diagonal = dx !== 0 && dy !== 0;
          if (diagonal) {
            // No corner-cutting: both orthogonal neighbours must be open, else
            // the diagonal clips a wall corner the feet-sampled walk would catch.
            if (g[cy * W + nx] === 1 || g[ny * W + cx] === 1) continue;
          }
          const tentative = gScore[cur]! + (diagonal ? Math.SQRT2 : 1);
          if (tentative < gScore[ni]!) {
            gScore[ni] = tentative;
            came[ni] = cur;
            open.push(ni, tentative + heur(ni));
          }
        }
      }
    }
    if (!reached) return null;

    // Reconstruct goal→start, then reverse. Drop the start cell (we're on it)
    // and convert each remaining cell to its world-px centre.
    const cells: number[] = [];
    for (let c = goal; c !== -1; c = came[c]!) {
      cells.push(c);
      if (c === start) break;
    }
    cells.reverse();
    const pts: { x: number; y: number }[] = [];
    for (let k = 1; k < cells.length; k++) {
      const c = cells[k]!;
      pts.push({
        x: (c % W) * COLLISION_SCALE + COLLISION_SCALE / 2,
        y: ((c / W) | 0) * COLLISION_SCALE + COLLISION_SCALE / 2,
      });
    }
    // Finish on the exact click target, not the last cell's centre.
    if (pts.length > 0) pts[pts.length - 1] = { x: tx, y: ty };
    else pts.push({ x: tx, y: ty });

    return this.simplifyPath(sx, sy, pts);
  }

  /** String-pull a dense cell path into a few long straight runs: keep a
   *  waypoint only where the straight line from the current anchor to the next
   *  node would cross an obstacle. Turns the grid's stair-steps into smooth
   *  diagonal walks. */
  private simplifyPath(
    sx: number,
    sy: number,
    pts: { x: number; y: number }[],
  ): { x: number; y: number }[] {
    if (pts.length <= 1) return pts;
    const out: { x: number; y: number }[] = [];
    let ax = sx;
    let ay = sy;
    for (let k = 1; k < pts.length; k++) {
      if (!this.lineClear(ax, ay, pts[k]!.x, pts[k]!.y)) {
        const anchor = pts[k - 1]!;
        out.push(anchor);
        ax = anchor.x;
        ay = anchor.y;
      }
    }
    out.push(pts[pts.length - 1]!);
    return out;
  }

  /** True if the straight segment a→b stays on walkable ground, feet-sampled
   *  every half-cell to match how movement collides. */
  private lineClear(ax: number, ay: number, bx: number, by: number): boolean {
    const dx = bx - ax;
    const dy = by - ay;
    const steps = Math.max(1, Math.ceil(Math.hypot(dx, dy) / (COLLISION_SCALE / 2)));
    for (let k = 0; k <= steps; k++) {
      const t = k / steps;
      if (this.blocked(ax + dx * t, ay + dy * t)) return false;
    }
    return true;
  }

  /** Keep an arrival point OUT of any warp-zone rectangle. A door's trigger only
   *  fires once the player has left it and walked back in (the anti-bounce arm),
   *  so if you spawn inside one, easy on maps like KaibaCorp Stadium where the
   *  exit zone fills a whole corner, walking toward that exit never re-arms it and
   *  you're stuck. Push the spawn just clear of the zone, toward the map centre,
   *  then re-nudge to walkable. */
  private clearArrivalZone(p: { x: number; y: number }): { x: number; y: number } {
    const hit = this.portals.find(
      (q) =>
        q.rect &&
        p.x >= q.rect.x0 - PORTAL_PAD &&
        p.x <= q.rect.x1 + PORTAL_PAD &&
        p.y >= q.rect.y0 - PORTAL_PAD &&
        p.y <= q.rect.y1 + PORTAL_PAD,
    );
    if (!hit?.rect) return p;
    const r = hit.rect;
    let vx = this.mapWidth / 2 - p.x;
    let vy = this.mapHeight / 2 - p.y;
    const len = Math.hypot(vx, vy) || 1;
    vx /= len;
    vy /= len;
    // Distance to step past the rect edge we're heading toward.
    const tx = vx > 0 ? (r.x1 - p.x) / vx : vx < 0 ? (r.x0 - p.x) / vx : Infinity;
    const ty = vy > 0 ? (r.y1 - p.y) / vy : vy < 0 ? (r.y0 - p.y) / vy : Infinity;
    const t = Math.min(tx, ty);
    if (!Number.isFinite(t) || t <= 0) return p; // centre lies inside the zone, give up
    const out = t + PORTAL_PAD + 64; // clear the edge + trigger pad + a margin
    return this.nudgeToWalkable(Math.round(p.x + vx * out), Math.round(p.y + vy * out));
  }

  /** Bind each authored door (destination + a rough hand coord, from mapGraph) to
   *  the real atari warp zone nearest it, so the trigger sits exactly on the
   *  doorway art instead of a hand-guessed circle. Greedy nearest-first, one zone
   *  per door, capped at ZONE_BIND_MAX, close enough to rescue a door placed at a
   *  map's centre when its real exit is in a corner, tight enough not to grab an
   *  unrelated exit. Doors with no zone in range keep the legacy circle (nudged to
   *  walkable). Destinations are never changed, only where/how the trigger fires.
   *  Portals stay INVISIBLE: the map art already draws the doors. */
  private buildPortals(): void {
    const authored = portalsFor(this.mapId);
    const zones = this.warpZones.map((z) => ({ z, taken: false }));
    this.portals = authored.map((raw) => ({
      to: raw.to,
      label: raw.label,
      cx: raw.rect ? (raw.rect[0] + raw.rect[2]) / 2 : raw.x,
      cy: raw.rect ? (raw.rect[1] + raw.rect[3]) / 2 : raw.y,
      // Scene maps author the trigger rect directly; legacy maps get it from atari
      // binding below (null for now).
      rect: raw.rect
        ? { x0: raw.rect[0], y0: raw.rect[1], x1: raw.rect[2], y1: raw.rect[3] }
        : (null as ActivePortal["rect"]),
      radius: raw.radius ?? 64,
    }));

    // All (door, zone) candidate pairs, bound greedily by ascending distance. Doors
    // that already carry an authored rect (scene maps) are excluded — they're placed.
    const pairs: { pi: number; zi: number; d: number }[] = [];
    authored.forEach((raw, pi) => {
      if (raw.rect) return;
      zones.forEach((zz, zi) => {
        pairs.push({ pi, zi, d: Math.hypot(zz.z.cx - raw.x, zz.z.cy - raw.y) });
      });
    });
    pairs.sort((a, b) => a.d - b.d);
    const boundDoor = new Set<number>();
    for (const { pi, zi, d } of pairs) {
      const slot = zones[zi];
      const door = this.portals[pi];
      if (!slot || !door || d > ZONE_BIND_MAX || boundDoor.has(pi) || slot.taken) continue;
      const z = slot.z;
      boundDoor.add(pi);
      slot.taken = true;
      door.rect = { x0: z.x0, y0: z.y0, x1: z.x1, y1: z.y1 };
      door.cx = z.cx;
      door.cy = z.cy;
    }

    // Doors with no zone in range fall back to the (nudged) hand circle.
    for (const p of this.portals) {
      if (p.rect) continue;
      const safe = this.nudgeToWalkable(p.cx, p.cy);
      p.cx = safe.x;
      p.cy = safe.y;
    }

    if (import.meta.env.DEV) {
      const unbound = this.portals.filter((p) => !p.rect).map((p) => p.to);
      const latent = zones.filter((z) => !z.taken).length;
      if (unbound.length || latent) {
        console.info(
          `[portals] ${this.mapId}: ${this.portals.length - unbound.length}/${this.portals.length} doors on atari zones` +
            (unbound.length ? `; circle fallback → ${unbound.join(", ")}` : "") +
            (latent ? `; ${latent} unused atari zone(s)` : ""),
        );
      }
    }
  }

  /** Where to drop the player after walking a door from `fromMap` into `toMap`:
   *  just inside `toMap`'s door back to `fromMap`, nudged toward the map centre
   *  and facing inward, so you step out next to the matching door instead of
   *  teleporting to the map centre. Falls back to the map's default spawn when
   *  there is no reciprocal door (e.g. one-way / map-disk-only areas). */
  private arrivalSpawn(toMap: string, fromMap: string): SpawnPoint {
    const back = portalsFor(toMap).find((p) => p.to === fromMap);
    const scene = sceneMap(toMap);
    const map = scene ? null : findMap(this.opts.data, toMap);
    if (!back || (!scene && !map)) return spawnFor(toMap);
    const w = scene ? scene.width : map!.cols * map!.partsW;
    const h = scene ? scene.height : map!.rows * map!.partsH;
    let vx = w / 2 - back.x;
    let vy = h / 2 - back.y;
    const len = Math.hypot(vx, vy) || 1;
    vx /= len;
    vy /= len;
    const OFFSET = 150; // step clear of the return door's own trigger radius
    const facing = facingFromVector(vx, vy);
    return {
      x: Math.round(back.x + vx * OFFSET),
      y: Math.round(back.y + vy * OFFSET),
      facing,
    };
  }

  // ── input ───────────────────────────────────────────────────────────────────
  private installClickToWalk(): void {
    const hit = new Graphics()
      .rect(0, 0, this.mapWidth, this.mapHeight)
      .fill({ color: 0x000000, alpha: 0.001 });
    hit.eventMode = "static";
    hit.on("pointertap", (e) => {
      const p = e.getLocalPosition(this.camera);
      const cx = clamp(p.x, 0, this.mapWidth);
      const cy = clamp(p.y, 0, this.mapHeight);
      if (!this.collision) {
        // No baked collision (data missing / still loading): straight-line walk.
        this.walkPath = [{ x: cx, y: cy }];
        return;
      }
      // Snap a click on a wall/void to the nearest walkable spot, then route to
      // it around obstacles. If it's genuinely unreachable from here, ignore the
      // click rather than beeline into a wall (the old run-into-obstacles bug).
      const t = this.nudgeToWalkable(cx, cy);
      const path = this.computePath(this.localX, this.localY, t.x, t.y);
      if (path) this.walkPath = path;
    });
    this.camera.addChildAt(hit, 1); // above base tiles, below avatars
  }

  // Virtual joystick (mobile): normalized [-1,1] steer vector. Engaging the
  // stick cancels a click-to-walk route, exactly like pressing a movement key.
  private stickX = 0;
  private stickY = 0;
  setStick(x: number, y: number): void {
    if ((x !== 0 || y !== 0) && this.walkPath.length > 0) this.walkPath = [];
    this.stickX = x;
    this.stickY = y;
  }

  private installInput(): void {
    const kd = (e: KeyboardEvent): void => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      const k = e.key.toLowerCase();
      if (["w", "a", "s", "d", "arrowup", "arrowdown", "arrowleft", "arrowright"].includes(k)) {
        this.walkPath = [];
        this.keys.add(k);
      }
    };
    const ku = (e: KeyboardEvent): void => {
      this.keys.delete(e.key.toLowerCase());
    };
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    this.unbinds.push(() => window.removeEventListener("keydown", kd));
    this.unbinds.push(() => window.removeEventListener("keyup", ku));
  }

  // ── per-frame ─────────────────────────────────────────────────────────────
  private update(dtMs: number): void {
    if (!this.localAvatar || this.transitioning || this.paused) return;

    // Distance to advance this frame, in world px (speed × clamped frame time).
    const step = WALK_SPEED * (Math.min(dtMs, MAX_FRAME_MS) / 1000);

    let dx = 0;
    let dy = 0;
    if (this.keys.has("w") || this.keys.has("arrowup")) dy -= 1;
    if (this.keys.has("s") || this.keys.has("arrowdown")) dy += 1;
    if (this.keys.has("a") || this.keys.has("arrowleft")) dx -= 1;
    if (this.keys.has("d") || this.keys.has("arrowright")) dx += 1;

    // Virtual joystick steers when no key is held (the normalization below
    // flattens its magnitude to full walk speed — the walk anim is one pace).
    if (dx === 0 && dy === 0) {
      dx = this.stickX;
      dy = this.stickY;
    }

    if (dx === 0 && dy === 0 && this.walkPath.length > 0) {
      // Pop any waypoints we're already within a frame's reach of, then steer at
      // the next one, so the avatar glides through corners instead of stalling
      // on each node of the route.
      while (
        this.walkPath.length > 0 &&
        Math.hypot(this.walkPath[0]!.x - this.localX, this.walkPath[0]!.y - this.localY) <= step
      ) {
        this.walkPath.shift();
      }
      if (this.walkPath.length > 0) {
        dx = this.walkPath[0]!.x - this.localX;
        dy = this.walkPath[0]!.y - this.localY;
      }
    }

    const moving = dx !== 0 || dy !== 0;
    if (moving) {
      const len = Math.hypot(dx, dy) || 1;
      const px = this.localX;
      const py = this.localY;
      // Resolve each axis independently against the collision grid so the player
      // slides along a wall instead of stopping dead when pushing into it
      // diagonally. Feet point = (localX, localY), what the atari mask blocks.
      const nx = clamp(this.localX + (dx / len) * step, 0, this.mapWidth);
      if (!this.blocked(nx, this.localY)) this.localX = nx;
      const ny = clamp(this.localY + (dy / len) * step, 0, this.mapHeight);
      if (!this.blocked(this.localX, ny)) this.localY = ny;
      const advanced = this.localX !== px || this.localY !== py;

      const facing = facingFromVector(dx, dy);
      if (facing !== this.localFacing) {
        this.localFacing = facing;
        this.localAvatar.setFacing(facing);
      }
      this.localContainer.x = this.localX;
      this.localContainer.y = this.localY;
      this.localContainer.zIndex = this.localY;
      this.localAvatar.walking = advanced;

      // Click-walking but wedged against an obstacle, drop the route so we don't
      // moon-walk in place forever trying to reach an unreachable spot. The A*
      // route avoids static walls, so this only trips on a freak rounding case.
      if (!advanced && this.walkPath.length > 0) {
        this.walkPath = [];
      }
      if (advanced) {
        const now = performance.now();
        if (now - this.lastSentMove > 50) {
          this.lastSentMove = now;
          this.opts.socket.send({ t: "Move", x: this.localX | 0, y: this.localY | 0, facing });
        }
      }
    } else {
      this.localAvatar.walking = false;
    }
    this.localAvatar.tick(dtMs);
    this.checkPortals();

    for (const r of this.players.values()) {
      const ddx = r.targetX - r.container.x;
      const ddy = r.targetY - r.container.y;
      if (Math.abs(ddx) > 0.5 || Math.abs(ddy) > 0.5) {
        r.container.x += ddx * 0.25;
        r.container.y += ddy * 0.25;
        r.container.zIndex = r.container.y;
        r.avatar.walking = true;
      } else {
        r.avatar.walking = false;
      }
      r.avatar.tick(dtMs);
    }

    // Ease the camera toward the player instead of locking 1:1, exp smoothing so
    // the chase feels identical at 60Hz and 144Hz. Floored on assign for crisp tiles.
    const target = this.cameraTarget();
    const k = 1 - Math.exp((-CAM_LERP * Math.min(dtMs, MAX_FRAME_MS)) / 1000);
    this.camX += (target.x - this.camX) * k;
    this.camY += (target.y - this.camY) * k;
    this.camera.x = this.camX | 0;
    this.camera.y = this.camY | 0;

    this.tickBubbles(performance.now());
  }

  /** Where the camera wants to sit this frame (un-floored, un-eased). Maps smaller
   *  than the viewport are centred; larger maps scroll with the player, clamped to
   *  the map edges. */
  private cameraTarget(): { x: number; y: number } {
    const viewW = this.app.screen.width;
    const viewH = this.app.screen.height;
    const scale = this.worldScale;
    const visibleWorldW = viewW / scale;
    const visibleWorldH = viewH / scale;
    return {
      x:
        this.mapWidth <= visibleWorldW
          ? (viewW - this.mapWidth * scale) / 2
          : -clamp(this.localX - visibleWorldW / 2, 0, this.mapWidth - visibleWorldW) * scale,
      y:
        this.mapHeight <= visibleWorldH
          ? (viewH - this.mapHeight * scale) / 2
          : -clamp(this.localY - visibleWorldH / 2, 0, this.mapHeight - visibleWorldH) * scale,
    };
  }

  private checkPortals(): void {
    if (this.transitioning || this.portals.length === 0) return;
    let insideAny = false;
    for (const portal of this.portals) {
      const inside = portal.rect
        ? this.localX >= portal.rect.x0 - PORTAL_PAD &&
          this.localX <= portal.rect.x1 + PORTAL_PAD &&
          this.localY >= portal.rect.y0 - PORTAL_PAD &&
          this.localY <= portal.rect.y1 + PORTAL_PAD
        : Math.hypot(this.localX - portal.cx, this.localY - portal.cy) <= portal.radius;
      if (inside) {
        insideAny = true;
        if (this.portalArmed) {
          this.transitioning = true;
          void this.enterMap(portal.to, this.arrivalSpawn(portal.to, this.mapId));
          return;
        }
      }
    }
    if (!insideAny) this.portalArmed = true;
  }

  // ── network ─────────────────────────────────────────────────────────────────
  private bindNetwork(): void {
    const s = this.opts.socket;
    const offEnter = s.on("EnterMapOk", (m) => {
      this.localX = m.x;
      this.localY = m.y;
      this.localFacing = m.facing;
      this.localContainer.x = m.x;
      this.localContainer.y = m.y;
      this.localContainer.zIndex = m.y;
      this.localAvatar?.setFacing(m.facing);
      this.portalArmed = false;
      this.emitHud();
    });
    const offSnap = s.on("MapSnapshot", (m) => {
      for (const p of m.players) {
        if (p.userId === this.opts.loginOk.userId) continue; // never duplicate self
        void this.spawnRemote(p);
      }
      this.emitHud();
    });
    const offSpawn = s.on("Spawn", (m) => {
      if (m.player.userId === this.opts.loginOk.userId) {
        // The server re-broadcasts Spawn after AvatarSet with the player's authoritative new look
        // (post-sanitize). Push it into the local chibi so a saved customizer change shows without
        // needing a refresh. Bust already updates reactively via session.loginOk.avatar.
        const look = chibiFrom(m.player.avatar);
        this.localAvatar?.setLook(look.gender, look.selection);
        return;
      }
      const existing = this.players.get(m.player.userId);
      if (existing) {
        existing.targetX = m.player.x;
        existing.targetY = m.player.y;
        return;
      }
      void this.spawnRemote(m.player);
      this.emitHud();
    });
    const offDespawn = s.on("Despawn", (m) => {
      const r = this.players.get(m.userId);
      if (!r) return;
      r.container.destroy({ children: true });
      this.players.delete(m.userId);
      this.emitHud();
    });
    const offMove = s.on("Move", (m) => {
      const r = this.players.get(m.userId);
      if (!r) return;
      r.targetX = m.x;
      r.targetY = m.y;
      r.avatar.setFacing(m.facing);
    });
    const offStatus = s.on("PlayerStatus", (m) => {
      const r = this.players.get(m.userId);
      if (!r) return;
      r.duelBadge.visible = m.dueling;
      r.avatar.setDueling(m.dueling); // strike the duel stance + raise the disk
    });
    // Re-enter the map after a reconnect. A dropped socket takes us out of the server's room
    // (endSession → onClientClose despawns us) and the reconnect only re-Auths — nothing puts us
    // back. Without this we stay invisible to everyone, and miss everyone who arrived during the
    // outage, until we happen to walk through a door. Auth is queued first (net.svelte.ts registers
    // its open handler at boot, before this one) and the server serializes per connection, so the
    // EnterMap always lands authenticated. Drop the stale remotes: MapSnapshot repopulates them.
    const offOpen = s.onOpen(() => {
      if (this.destroyed) return;
      for (const r of this.players.values()) r.container.destroy({ children: true });
      this.players.clear();
      this.emitHud();
      s.send({ t: "EnterMap", mapId: this.mapId, x: this.localX, y: this.localY, facing: this.localFacing });
    });
    this.unbinds.push(offEnter, offSnap, offSpawn, offDespawn, offMove, offStatus, offOpen);
  }

  private async spawnRemote(snap: PlayerSnap): Promise<void> {
    if (this.players.has(snap.userId) || this.destroyed) return;
    const look = chibiFrom(snap.avatar);
    const avatar = new ChibiAvatar({
      manifest: this.opts.data.chibi,
      gender: look.gender,
      selection: look.selection,
      displayHeight: AVATAR_DISPLAY_H,
    });
    avatar.setDueling(snap.dueling ?? false); // raise the duel disk if they're already mid-duel
    await avatar.build();
    const container = new Container();
    container.x = snap.x;
    container.y = snap.y;
    container.zIndex = snap.y;

    // Name label above the head, a distinct cyan tint marks a real player vs. an NPC.
    const H = AVATAR_DISPLAY_H;
    const label = new Text({
      text: snap.name,
      style: {
        fontFamily: "Rajdhani, system-ui, sans-serif",
        fontSize: 14,
        fontWeight: "700",
        fill: 0x86f0ff,
        stroke: { color: 0x04141c, width: 4 },
        align: "center",
      },
    });
    label.anchor.set(0.5, 1);
    label.y = -H + 6;
    label.resolution = 2;

    // Clickable hit area over the chibi → open the player's card.
    const w = H * 0.62;
    const hit = new Graphics().rect(-w / 2, -H + 4, w, H).fill({ color: 0x000000, alpha: 0.001 });
    hit.eventMode = "static";
    hit.cursor = "pointer";
    hit.on("pointertap", () => this.opts.onPlayerClick?.(snap.userId));
    hit.on("pointerover", () => (label.style.fill = 0xb6f8ff));
    hit.on("pointerout", () => (label.style.fill = 0x86f0ff));

    // "DUELING" badge above the name, shown while this player is in a duel so onlookers see
    // them busy in place rather than a frozen avatar. Built hidden; toggled by PlayerStatus.
    const duelBadge = buildDuelBadge();
    duelBadge.y = -H - 12;
    duelBadge.visible = snap.dueling ?? false;

    container.addChild(avatar.root, hit, label, duelBadge);
    this.midground.addChild(container);
    this.players.set(snap.userId, { avatar, container, targetX: snap.x, targetY: snap.y, name: snap.name, duelBadge });
    this.emitHud(); // async spawn resolved → refresh the count
  }

  /** Pop a speech bubble above whoever's chibi matches `speakerName`. The bubble is
   *  parented to the speaker's container so it walks with them and scrolls with the
   *  camera; replaces any prior bubble from the same speaker; auto-dismisses on a
   *  duration scaled to the line length, with a short fade at the tail. */
  showSpeechBubble(speakerName: string, text: string): void {
    const clean = text.trim();
    if (!clean) return;
    let parent: Container | null = null;
    if (speakerName === this.opts.loginOk.avatar.name) {
      parent = this.localContainer;
    } else {
      for (const r of this.players.values()) {
        if (r.name === speakerName) { parent = r.container; break; }
      }
    }
    if (!parent) return;

    const prev = this.bubbles.get(speakerName);
    if (prev) prev.view.destroy({ children: true });

    const view = buildSpeechBubble(clean);
    view.y = -AVATAR_DISPLAY_H - 4; // tail tip just above the head
    parent.addChild(view);

    // 2.4s base + ~50ms/char, capped, long lines stay readable, short ones don't linger.
    const lifetime = Math.min(9000, 2400 + clean.length * 50);
    const now = performance.now();
    this.bubbles.set(speakerName, { view, expiresAt: now + lifetime, fadeAt: now + lifetime - 280 });
  }

  /** Tick: fade out then drop expired bubbles. Cheap, typically 0–2 entries. */
  private tickBubbles(now: number): void {
    if (this.bubbles.size === 0) return;
    for (const [name, b] of this.bubbles) {
      if (now >= b.expiresAt) {
        b.view.destroy({ children: true });
        this.bubbles.delete(name);
      } else if (now >= b.fadeAt) {
        b.view.alpha = Math.max(0, (b.expiresAt - now) / 280);
      }
    }
  }

  // ── overworld NPCs ────────────────────────────────────────────────────────
  /** Stand the recovered character cast on the current map. NPCs are static and
   *  identical for everyone, so they're authored client-side (like portals), no
   *  server entity. Each shows a name label and is clickable; a tap forwards the
   *  WorldNpc to the Svelte overlay, which opens the conversation dialog. */
  private async spawnNpcs(): Promise<void> {
    const mapId = this.mapId;
    const list = npcsFor(mapId);
    if (list.length === 0) return;
    const presets = new Set(this.opts.data.chibi.npc?.presets.map((p) => p.preset) ?? []);
    const H = AVATAR_DISPLAY_H; // same size as the player chibi

    for (const npc of list) {
      if (!presets.has(npc.char)) continue; // no chibi sprite for this character
      const chibi = new NpcChibi({
        manifest: this.opts.data.chibi,
        preset: npc.char,
        displayHeight: H,
        facing: npc.facing ?? "s",
      });
      await chibi.build();
      if (this.destroyed || this.mapId !== mapId) {
        chibi.destroy();
        return; // warped away mid-load, drop this (and the rest of the batch).
      }

      const container = new Container();
      container.x = npc.x;
      container.y = npc.y;
      container.zIndex = npc.y; // Y-sort with players/objects

      // Name label above the head (brightens on hover to signal interactivity).
      const label = new Text({
        text: npc.name,
        style: {
          fontFamily: "Rajdhani, system-ui, sans-serif",
          fontSize: 14,
          fontWeight: "700",
          fill: 0xdff6ff,
          stroke: { color: 0x04141c, width: 4 },
          align: "center",
        },
      });
      label.anchor.set(0.5, 1);
      label.y = -H + 6;
      label.resolution = 2;

      // Clickable hit area over the chibi (container space: feet at y≈0, head at -H).
      const w = H * 0.62;
      const hit = new Graphics().rect(-w / 2, -H + 4, w, H).fill({ color: 0x000000, alpha: 0.001 });
      hit.eventMode = "static";
      hit.cursor = "pointer";
      hit.on("pointertap", () => this.opts.onNpcClick?.(npc));
      hit.on("pointerover", () => (label.style.fill = 0x66f6ff));
      hit.on("pointerout", () => (label.style.fill = 0xdff6ff));

      container.addChild(chibi.root, hit, label);
      this.midground.addChild(container);
      this.npcs.push({ container, chibi });
    }
  }

  // ── interactive fixtures (card shop, …) ─────────────────────────────────────
  /** Place this map's clickable fixtures: a transparent hit rectangle over the
   *  art, a persistent floating label, and a hover outline. A tap forwards the
   *  fixture to the Svelte overlay (World.svelte), which opens the right panel. */
  private spawnInteractables(): void {
    for (const it of interactablesFor(this.mapId)) {
      const container = new Container();

      const hit = new Graphics().rect(it.x, it.y, it.w, it.h).fill({ color: 0x000000, alpha: 0.001 });
      hit.eventMode = "static";
      hit.cursor = "pointer";
      hit.on("pointertap", () => this.opts.onInteract?.(it.kind, it.id));
      // Hover affordance: brighten the floating label (no hard outline rectangle,
      // the pointer cursor + the always-on label already read as clickable).
      hit.on("pointerover", () => {
        label.style.fill = 0x9ffcff;
      });
      hit.on("pointerout", () => {
        label.style.fill = 0xdff6ff;
      });

      // Persistent label above the fixture (always visible so players find it).
      const label = new Text({
        text: it.label.toUpperCase(),
        style: {
          fontFamily: "Rajdhani, system-ui, sans-serif",
          fontSize: 18,
          fontWeight: "700",
          letterSpacing: 2,
          fill: 0xdff6ff,
          stroke: { color: 0x04141c, width: 5 },
          align: "center",
        },
      });
      label.anchor.set(0.5, 1);
      label.x = it.x + it.w / 2;
      label.y = it.y - 4;
      label.resolution = 2;

      container.addChild(hit, label);
      // Overlay layer → label floats above the building art and the player.
      this.overlay.addChild(container);
      this.interactables.push(container);
    }
  }

  destroy(): void {
    this.destroyed = true;
    for (const u of this.unbinds) u();
    this.unbinds = [];
    // Guard the Pixi teardown: a throw here runs inside the Svelte {#key} screen swap and
    // would blank the whole app. The TexturePool return path is already hardened (main.ts),
    // but keep this as a backstop so no teardown error can ever take the UI down.
    try {
      // NOTE: `{ removeView: true }`, NOT `true`. `app.destroy(true)` makes Pixi call
      // GlobalResourceRegistry.release(), which clears the GLOBAL pools (TexturePool, the
      // batcher geometry, …) shared by EVERY live Pixi app, nuking a sibling renderer's
      // geometry mid-frame (→ "Cannot read properties of null (reading 'geometry')") and
      // blanking its canvas. removeView frees only THIS renderer's context + canvas.
      if (this.app.renderer) this.app.destroy({ removeView: true });
    } catch (e) {
      console.error("[WorldRenderer] destroy failed (ignored)", e);
    }
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** 8-way facing from a movement vector (screen space: +x right, +y down). Each
 *  axis only contributes when it's a real fraction of the other (tan 22.5° ≈
 *  0.4142), so near-cardinal motion stays cardinal and only true diagonals get a
 *  diagonal facing. Vertical component is named first ("ne", "sw"). */
function facingFromVector(dx: number, dy: number): Facing {
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);
  const ns = ady > adx * 0.4142 ? (dy < 0 ? "n" : "s") : "";
  const ew = adx > ady * 0.4142 ? (dx < 0 ? "w" : "e") : "";
  return ((ns + ew) as Facing) || "s";
}

/** Aesthetic white speech bubble: rounded panel + a small downward tail tip at the
 *  container origin (0,0), so positioning is "tail-tip to head-top". Wraps at a
 *  comfortable reading width; lines that already fit stay tight to the text. */
function buildSpeechBubble(text: string): Container {
  const PAD_X = 11;
  const PAD_Y = 7;
  const RADIUS = 11;
  const MAX_W = 240;
  const TAIL_H = 8;
  const TAIL_HALF = 6;

  const c = new Container();
  const t = new Text({
    text,
    style: {
      fontFamily: "Rajdhani, system-ui, sans-serif",
      fontSize: 14,
      fontWeight: "600",
      fill: 0x1a2433,
      align: "center",
      wordWrap: true,
      wordWrapWidth: MAX_W - PAD_X * 2,
      breakWords: true,
      lineHeight: 18,
    },
  });
  t.resolution = 2;

  const w = Math.ceil(t.width) + PAD_X * 2;
  const h = Math.ceil(t.height) + PAD_Y * 2;
  const top = -TAIL_H - h;

  // Soft shadow: a duplicate body offset 2px down, dark + transparent.
  const shadow = new Graphics()
    .roundRect(-w / 2 + 1, top + 2, w, h, RADIUS)
    .fill({ color: 0x000000, alpha: 0.28 });

  // Body, bright white, just shy of opaque so it sits on the world without screaming.
  const body = new Graphics()
    .roundRect(-w / 2, top, w, h, RADIUS)
    .fill({ color: 0xffffff, alpha: 0.97 });

  // Tail: a small triangle, white, drawn on top of the body so the shared seam
  // with the body's bottom edge disappears under the same fill.
  const tail = new Graphics()
    .moveTo(-TAIL_HALF, -TAIL_H)
    .lineTo(0, 0)
    .lineTo(TAIL_HALF, -TAIL_H)
    .closePath()
    .fill({ color: 0xffffff, alpha: 0.97 });

  // Subtle outline along the body + the two tail edges (skip the top of the tail
  // where it meets the body, so they read as one shape).
  const outline = new Graphics()
    .roundRect(-w / 2, top, w, h, RADIUS)
    .stroke({ color: 0x0b1626, alpha: 0.22, width: 1.25 })
    .moveTo(-TAIL_HALF, -TAIL_H)
    .lineTo(0, 0)
    .lineTo(TAIL_HALF, -TAIL_H)
    .stroke({ color: 0x0b1626, alpha: 0.22, width: 1.25 });

  t.anchor.set(0.5, 0.5);
  t.x = 0;
  t.y = top + h / 2;

  c.addChild(shadow, body, tail, outline, t);
  return c;
}

/** A small gold "DUELING" pill floated over a player who's mid-duel, anchored centre, so the
 *  caller just sets its `y` above the nameplate. */
function buildDuelBadge(): Container {
  const c = new Container();
  const t = new Text({
    text: "DUELING",
    style: {
      fontFamily: "Rajdhani, system-ui, sans-serif",
      fontSize: 11,
      fontWeight: "700",
      fill: 0xffd23c,
      align: "center",
      letterSpacing: 1.5,
    },
  });
  t.anchor.set(0.5, 0.5);
  t.resolution = 2;
  const w = Math.ceil(t.width) + 14;
  const h = 17;
  const bg = new Graphics()
    .roundRect(-w / 2, -h / 2, w, h, 4)
    .fill({ color: 0x2a1c04, alpha: 0.9 })
    .stroke({ color: 0xffd23c, alpha: 0.85, width: 1 });
  c.addChild(bg, t);
  return c;
}

/** Tiny binary min-heap for A*: stores cell indices keyed by f-score. Pushes are
 *  lazy (no decrease-key), stale entries are skipped on pop via A*'s closed set. */
class MinHeap {
  private items: number[] = [];
  private keys: number[] = [];

  size(): number {
    return this.items.length;
  }

  push(item: number, key: number): void {
    let i = this.items.length;
    this.items.push(item);
    this.keys.push(key);
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.keys[p]! <= this.keys[i]!) break;
      this.swap(i, p);
      i = p;
    }
  }

  pop(): number {
    const top = this.items[0]!;
    const item = this.items.pop()!;
    const key = this.keys.pop()!;
    const n = this.items.length;
    if (n > 0) {
      this.items[0] = item;
      this.keys[0] = key;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let s = i;
        if (l < n && this.keys[l]! < this.keys[s]!) s = l;
        if (r < n && this.keys[r]! < this.keys[s]!) s = r;
        if (s === i) break;
        this.swap(i, s);
        i = s;
      }
    }
    return top;
  }

  private swap(a: number, b: number): void {
    const ti = this.items[a]!;
    this.items[a] = this.items[b]!;
    this.items[b] = ti;
    const tk = this.keys[a]!;
    this.keys[a] = this.keys[b]!;
    this.keys[b] = tk;
  }
}
